import { useNeedPay } from 'modules/admin';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import * as apiService from 'api/apiServices';
import { useToast } from 'context/ToastContext';
import type { ServiceEditType } from '../../types';

type SaveServiceCardDeps = {
    fetchServices: (force?: boolean) => Promise<unknown>;
    setService: Dispatch<SetStateAction<ServiceEditType | null>>;
    setShowEditService: Dispatch<SetStateAction<number | false>>;
};

export type SaveServiceCardOptions = {
    entityId?: number;
    /** Закрити картку після успішного збереження (кнопка «Зберегти») */
    close?: boolean;
};

export type SaveServiceCardResult =
    | { ok: true; entityId: number; values: ServiceEditType }
    | { ok: false };

/**
 * Спільне збереження картки послуги (валідація + API) для вкладки «Загальні» і «Файли».
 */
const useSaveServiceCard = ({ fetchServices, setService, setShowEditService }: SaveServiceCardDeps) => {
    const { t } = useTranslation(['Services', 'common']);
    const { showToast } = useToast();
    const { needPay } = useNeedPay();

    const saveServiceCard = async (
        values: ServiceEditType,
        options: SaveServiceCardOptions = {},
    ): Promise<SaveServiceCardResult> => {
        const article = String(values.servArticle || '').trim();
        const name = String(values.servName || '').trim();
        const dimensionId = Number(values.dimensionId || 0);
        const categoryId = Number(values.categoryId || 0);

        if (!article) {
            showToast(t('messEmptyArticle'), 'danger');
            return { ok: false };
        }
        if (!name) {
            showToast(t('messEmptyName'), 'danger');
            return { ok: false };
        }
        if (dimensionId <= 0) {
            showToast(t('messEmptyDimension'), 'danger');
            return { ok: false };
        }

        const entityId = Number(options.entityId ?? values.ID ?? 0);
        if (entityId <= 0 && needPay()) return { ok: false };
        const payload: ServiceEditType = {
            ...values,
            servArticle: article,
            servName: name,
            dimensionId,
            categoryId,
        };

        const resSave = await apiService.save(entityId, payload);
        if (!resSave) {
            showToast(t('common:messError'), 'danger');
            return { ok: false };
        }
        if (resSave.articleDublicate) {
            showToast(t('messArticleExists'), 'danger');
            return { ok: false };
        }
        if (!resSave.stat) {
            showToast(t('common:messError'), 'danger');
            return { ok: false };
        }

        const savedId = entityId > 0 ? entityId : Number(resSave.stat);
        if (!Number.isFinite(savedId) || savedId <= 0) {
            showToast(t('common:messError'), 'danger');
            return { ok: false };
        }

        const nextValues: ServiceEditType = { ...payload, ID: savedId };
        setService((prev) => (prev ? { ...prev, ...nextValues } : nextValues));
        await fetchServices(true);

        if (options.close) {
            setShowEditService(false);
            showToast(t('messServiceSaved'), 'success');
            setTimeout(() => {
                setService(null);
            }, 300);
        }

        return { ok: true, entityId: savedId, values: nextValues };
    };

    return { saveServiceCard };
};

export default useSaveServiceCard;
